import React, { useState } from 'react'
import { useQuery, useMutation } from '@apollo/react-hooks'
import gql from 'graphql-tag'
import {
	Paper, Table, TableBody, TableCell, TableHead, TableRow,
	TextField, Button, IconButton, Typography, Grid,
	CircularProgress, Snackbar, MenuItem, Select,
	FormControl, InputLabel
} from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from '@material-ui/icons/Add'
import Alert from '@material-ui/lab/Alert'
import { ADD_COMMON_NAME, DELETE_COMMON_NAME } from '../../libs/Mutations'

const COMMON_NAMES_QUERY = gql`
query get_common_names($manufacturerId: Int!) {
  admin(manufacturerId: $manufacturerId) {
    commonNames(manufacturerId: $manufacturerId) {
      nameId
      cerName
      manufacturerId
      isActive
    }
  }
}
`

const MANUFACTURERS_QUERY = gql`
query get_manufacturers_for_sync($filter: String) {
  admin {
    manufacturers(filter: $filter) {
      manufacturerId
      displayName
      isActive
    }
  }
}
`

const styles = (theme) => ({
	root: {
		flexGrow: 1,
		padding: theme.spacing(3),
	},
	title: {
		marginBottom: theme.spacing(2),
	},
	filterRow: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(2),
		marginBottom: theme.spacing(3),
	},
	addRow: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		marginBottom: theme.spacing(2),
	},
	selectControl: {
		minWidth: 300,
	},
	addInput: {
		minWidth: 300,
	},
	emptyState: {
		padding: theme.spacing(4),
		textAlign: 'center',
		color: theme.palette.grey[500],
	},
})

const GlobalCommonNames = ({ classes }) => {
	const [selectedManufacturerId, setSelectedManufacturerId] = useState('')
	const [newName, setNewName] = useState('')
	const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

	const { data: mfrData, loading: mfrLoading } = useQuery(
		MANUFACTURERS_QUERY,
		{ variables: { filter: null }, fetchPolicy: 'cache-first' }
	)

	const { data: cnData, loading: cnLoading, refetch: refetchCn } = useQuery(
		COMMON_NAMES_QUERY,
		{
			variables: { manufacturerId: parseInt(selectedManufacturerId) || 0 },
			skip: !selectedManufacturerId,
			fetchPolicy: 'network-only',
		}
	)

	const [addCommonName, { loading: adding }] = useMutation(ADD_COMMON_NAME, {
		onCompleted: () => {
			setNewName('')
			refetchCn()
			setSnackbar({ open: true, message: 'Common name added', severity: 'success' })
		},
		onError: (err) => setSnackbar({ open: true, message: err.message, severity: 'error' }),
	})

	const [deleteCommonName, { loading: deleting }] = useMutation(DELETE_COMMON_NAME, {
		onCompleted: () => {
			refetchCn()
			setSnackbar({ open: true, message: 'Common name removed', severity: 'success' })
		},
		onError: (err) => setSnackbar({ open: true, message: err.message, severity: 'error' }),
	})

	const handleAdd = () => {
		const trimmed = newName.trim()
		if (!trimmed || !selectedManufacturerId) return
		addCommonName({
			variables: {
				manufacturerId: parseInt(selectedManufacturerId),
				cerName: trimmed,
			},
		})
	}

	const handleDelete = (nameId) => {
		if (!window.confirm('Remove this common name?')) return
		deleteCommonName({
			variables: {
				nameId: parseInt(nameId),
				manufacturerId: parseInt(selectedManufacturerId),
			},
		})
	}

	const manufacturers = mfrData?.admin?.manufacturers || []
	const commonNames = cnData?.admin?.commonNames || []
	const selectedMfr = manufacturers.find(m => m.manufacturerId === parseInt(selectedManufacturerId))

	return (
		<div className={classes.root}>
			<Typography variant="h4" className={classes.title}>
				Manufacturer Common Names
			</Typography>
			<Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
				Manage CEC brand name aliases per manufacturer. These names are used to match
				and import products from the CEC approved list during sync.
			</Typography>

			<div className={classes.filterRow}>
				<FormControl variant="outlined" size="small" className={classes.selectControl}>
					<InputLabel>Select Manufacturer</InputLabel>
					{mfrLoading ? (
						<Select value="" label="Select Manufacturer" disabled>
							<MenuItem value="">Loading...</MenuItem>
						</Select>
					) : (
						<Select
							value={selectedManufacturerId}
							onChange={(e) => setSelectedManufacturerId(e.target.value)}
							label="Select Manufacturer"
						>
							<MenuItem value="">-- Select --</MenuItem>
							{manufacturers.map((m) => (
								<MenuItem key={m.manufacturerId} value={m.manufacturerId}>
									{m.displayName} {!m.isActive && '(inactive)'}
								</MenuItem>
							))}
						</Select>
					)}
				</FormControl>
			</div>

			{selectedManufacturerId && (
				<>
					<Typography variant="h6" gutterBottom>
						{selectedMfr?.displayName} - Common Names ({commonNames.length})
					</Typography>

					<Grid container spacing={1} alignItems="center" className={classes.addRow}>
						<Grid item>
							<TextField
								className={classes.addInput}
								variant="outlined"
								size="small"
								label="New brand name"
								value={newName}
								onChange={(e) => setNewName(e.target.value)}
								onKeyPress={(e) => { if (e.key === 'Enter') handleAdd() }}
								disabled={adding}
							/>
						</Grid>
						<Grid item>
							<Button
								variant="contained"
								color="primary"
								startIcon={adding ? <CircularProgress size={18} /> : <AddIcon />}
								onClick={handleAdd}
								disabled={adding || !newName.trim()}
							>
								Add
							</Button>
						</Grid>
					</Grid>

					{cnLoading ? (
						<div style={{ textAlign: 'center', padding: 32 }}><CircularProgress /></div>
					) : commonNames.length === 0 ? (
						<Paper elevation={0} className={classes.emptyState}>
							<Typography variant="body1">No common names for this manufacturer.</Typography>
						</Paper>
					) : (
						<Paper elevation={0} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Brand Name (CerName)</TableCell>
										<TableCell>Status</TableCell>
										<TableCell align="right">Actions</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{commonNames.map((row) => (
										<TableRow key={row.nameId}>
											<TableCell>{row.cerName}</TableCell>
											<TableCell>{row.isActive === '1' || row.isActive === 'True' ? 'Active' : row.isActive || 'Active'}</TableCell>
											<TableCell align="right">
												<IconButton
													size="small"
													onClick={() => handleDelete(row.nameId)}
													disabled={deleting}
													title="Remove"
												>
													<DeleteIcon fontSize="small" />
												</IconButton>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</Paper>
					)}
				</>
			)}

			<Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					severity={snackbar.severity}
					variant="filled"
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</div>
	)
}

export default withStyles(styles)(GlobalCommonNames)
